import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Row, Col, Table, Input, Button, Typography, Tag, Space, Spin, Empty, message } from 'antd';
import { CloudServerOutlined, DownloadOutlined, ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api, { formatApiError } from '../utils/api';

const { Title, Text } = Typography;
const COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d', '#2f54eb', '#a0d911'];

export default function Distributors() {
  const [list, setList] = useState([]);
  const [none, setNone] = useState(0);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [songs, setSongs] = useState([]);
  const [songsLoading, setSongsLoading] = useState(false);
  const [q, setQ] = useState('');

  const activeRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/distributors');
      setList(res.data?.distributors || []);
      setNone(res.data?.no_distributor || 0);
    } catch (err) {
      if (!silent) message.error(formatApiError(err, 'Distributors load nahi hue'));
    } finally {
      setLoading(false);
    }
  };

  const refreshSongs = async () => {
    const d = activeRef.current;
    if (!d) return;
    try {
      const res = await api.get('/distributors/songs', { params: { name: d.key } });
      if (activeRef.current === d) setSongs(Array.isArray(res.data) ? res.data : []);
    } catch (err) { /* keep current list */ }
  };

  useEffect(() => {
    load();
    const refresh = () => { if (document.visibilityState === 'visible') { load(true); refreshSongs(); } };
    const t = setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(t); window.removeEventListener('focus', refresh); };
  }, []);

  const open = async (d) => {
    activeRef.current = d;
    setActive(d);
    setQ('');
    setSongs([]);
    setSongsLoading(true);
    try {
      const res = await api.get('/distributors/songs', { params: { name: d.key } });
      setSongs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      message.error(formatApiError(err, 'Songs load nahi hue'));
    } finally {
      setSongsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return songs;
    return songs.filter((r) => [r.title, r.singer, r.isrc, r.upc].some((v) => (v || '').toLowerCase().includes(s)));
  }, [songs, q]);

  const download = () => {
    const rows = filtered.map((r) => ({ Title: r.title, ISRC: r.isrc, UPC: r.upc }));
    const ws = XLSX.utils.json_to_sheet(rows, { header: ['Title', 'ISRC', 'UPC'] });
    ws['!cols'] = [{ wch: 45 }, { wch: 16 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Songs');
    const safe = active.label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'distributor';
    XLSX.writeFile(wb, `${safe}-songs.xlsx`);
  };

  const cards = [
    ...list.map((d) => ({ key: d.name, label: d.name, count: d.count, isrc: d.isrc, upc: d.upc })),
    ...(none ? [{ key: '__none__', label: 'Distributor nahi likha', count: none, muted: true }] : []),
  ];

  if (active) {
    return (
      <div>
        <Space style={{ marginBottom: 16 }} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => { activeRef.current = null; setActive(null); }}>Sab distributors</Button>
          <Title level={3} style={{ margin: 0 }}>{active.label}</Title>
          <Tag color="blue" style={{ fontSize: 14, padding: '2px 10px' }}>{songs.length} songs</Tag>
        </Space>
        <Card>
          <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
            <Input.Search allowClear placeholder="Title / singer / ISRC / UPC se search" value={q}
              onChange={(e) => setQ(e.target.value)} style={{ width: 340 }} />
            <Button type="primary" icon={<DownloadOutlined />} disabled={!filtered.length} onClick={download}>
              Excel Download (Title, ISRC, UPC)
            </Button>
          </Space>
          <Table
            rowKey={(r, i) => `${r.title}-${r.isrc}-${i}`}
            loading={songsLoading}
            dataSource={filtered}
            size="middle"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} songs` }}
            columns={[
              { title: '#', width: 60, render: (_, __, i) => i + 1 },
              { title: 'Title', dataIndex: 'title', sorter: (a, b) => a.title.localeCompare(b.title) },
              { title: 'Singer', dataIndex: 'singer' },
              { title: 'ISRC', dataIndex: 'isrc', render: (v) => v || <Text type="secondary">—</Text> },
              { title: 'UPC', dataIndex: 'upc', render: (v) => v || <Text type="secondary">—</Text> },
              { title: 'Release Date', dataIndex: 'release_date', render: (v) => v || '' },
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
        <Title level={3} style={{ margin: 0 }}><CloudServerOutlined /> Distributor Audio Data</Title>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </Space>
      <Spin spinning={loading}>
        {!cards.length && !loading ? <Empty description="Koi distributor nahi mila" /> : (
          <Row gutter={[16, 16]}>
            {cards.map((d, i) => (
              <Col xs={24} sm={12} md={8} lg={6} key={d.key}>
                <Card hoverable onClick={() => open(d)}
                  style={{ borderTop: `4px solid ${d.muted ? '#bfbfbf' : COLORS[i % COLORS.length]}`, height: '100%' }}>
                  <Text strong style={{ fontSize: 16 }}>{d.label}</Text>
                  <div style={{ fontSize: 32, fontWeight: 700, margin: '6px 0', color: d.muted ? '#8c8c8c' : COLORS[i % COLORS.length] }}>
                    {d.count}
                  </div>
                  <Text type="secondary">songs</Text>
                  {!d.muted && (
                    <div style={{ marginTop: 8 }}>
                      <Tag>ISRC: {d.isrc}</Tag><Tag>UPC: {d.upc}</Tag>
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Spin>
    </div>
  );
}
